<!-- HotX Console 0.1.115 | 2026-09-17 11:48:00 CST | parent 9e636af9840b931089933abb0df2cafb51764df5 -->
<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'
import {
  CONSOLE_SESSION_CHANGED_EVENT,
  hasConsoleSession,
  httpClient,
} from '../../api/client'

type TrafficQuota = {
  quotaBytes: number
  usedBytes: number
  remainingBytes: number
  usedPercent: number
  cycleStartedAt: string | null
  cycleEndsAt: string | null
  status: string
  upgradeRequired: boolean
}

type TrafficQuotaEnvelope = {
  data: TrafficQuota
}

const quota = ref<TrafficQuota | null>(null)
const loading = ref(true)
const error = ref('')
let timer: number | undefined

const formatBytes = (bytes: number) => {
  const gb = Math.max(0, bytes) / 1_000_000_000
  return `${gb >= 10 ? gb.toFixed(1) : gb.toFixed(2)} GB`
}
const cycleText = computed(() => {
  if (!quota.value?.cycleEndsAt) return '使用免费套餐后开始 7 天周期'
  return `本周期至 ${new Date(quota.value.cycleEndsAt).toLocaleString('zh-CN', { hour12: false })}`
})

async function loadQuota() {
  if (!hasConsoleSession()) {
    quota.value = null
    error.value = '登录后可查看免费流量配额'
    loading.value = false
    return
  }
  try {
    const response = await httpClient.get<TrafficQuota | TrafficQuotaEnvelope>('/api/v1/console/traffic-quota')
    quota.value = 'data' in response ? response.data : response
    if (!quota.value) throw new Error('流量配额暂时无法加载')
    error.value = ''
  } catch {
    quota.value = null
    error.value = hasConsoleSession() ? '流量配额暂时无法加载' : '登录后可查看免费流量配额'
  } finally {
    loading.value = false
  }
}

const handleSessionChanged = () => {
  loading.value = true
  void loadQuota()
}

onMounted(() => {
  void loadQuota()
  timer = window.setInterval(loadQuota, 30_000)
  window.addEventListener(CONSOLE_SESSION_CHANGED_EVENT, handleSessionChanged)
})
onUnmounted(() => {
  if (timer) window.clearInterval(timer)
  window.removeEventListener(CONSOLE_SESSION_CHANGED_EVENT, handleSessionChanged)
})
</script>

<template>
  <section class="traffic-quota-card" aria-labelledby="traffic-quota-title">
    <div class="quota-heading">
      <div>
        <p class="quota-kicker">FREE PLAN · 7 DAYS</p>
        <h2 id="traffic-quota-title">免费流量配额</h2>
        <p class="quota-cycle">{{ cycleText }}</p>
      </div>
      <span v-if="quota" class="quota-status" :class="{ danger: quota.upgradeRequired }">
        {{ quota.upgradeRequired ? '已用尽' : '计量正常' }}
      </span>
    </div>

    <div v-if="loading" class="quota-message">正在读取节点流量...</div>
    <div v-else-if="error" class="quota-message warning">{{ error }}</div>
    <template v-else-if="quota">
      <div class="quota-numbers">
        <div><span>已用</span><strong>{{ formatBytes(quota.usedBytes) }}</strong></div>
        <div><span>剩余</span><strong>{{ formatBytes(quota.remainingBytes) }}</strong></div>
        <div><span>周期额度</span><strong>{{ formatBytes(quota.quotaBytes) }}</strong></div>
      </div>
      <div class="quota-track" role="progressbar" :aria-valuenow="quota.usedPercent" aria-valuemin="0" aria-valuemax="100">
        <span :class="{ warning: quota.usedPercent >= 70, danger: quota.usedPercent >= 100 }" :style="{ width: `${Math.min(100, quota.usedPercent)}%` }" />
      </div>
      <div class="quota-footer">
        <p>{{ quota.usedPercent }}% 已使用。流量按节点实际成功输出统计。</p>
        <div class="quota-actions">
          <RouterLink to="/packages" class="primary">升级套餐</RouterLink>
          <RouterLink to="/help" class="secondary">联系我们</RouterLink>
        </div>
      </div>
    </template>
  </section>
</template>

<style scoped>
.traffic-quota-card{margin:0 0 20px;padding:24px;border:1px solid #cfe2d4;border-radius:18px;background:linear-gradient(135deg,#f8fcf7 0%,#fffaf0 100%);box-shadow:0 14px 34px rgba(20,63,43,.08);color:#16372a}.quota-heading,.quota-footer{display:flex;align-items:flex-start;justify-content:space-between;gap:18px}.quota-kicker{margin:0 0 5px;color:#16754e;font-size:12px;font-weight:800;letter-spacing:.12em}.quota-heading h2{margin:0;font-size:24px;line-height:1.25}.quota-cycle,.quota-footer p{margin:7px 0 0;color:#62756b;font-size:14px}.quota-status{padding:7px 12px;border-radius:999px;background:#e1f4e6;color:#14643f;font-size:13px;font-weight:800}.quota-status.danger{background:#ffe9e2;color:#a73520}.quota-numbers{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;margin:22px 0 16px}.quota-numbers div{padding:16px;border:1px solid rgba(35,112,74,.14);border-radius:13px;background:rgba(255,255,255,.72)}.quota-numbers span{display:block;color:#718078;font-size:13px}.quota-numbers strong{display:block;margin-top:6px;font-size:20px}.quota-track{height:10px;overflow:hidden;border-radius:999px;background:#dfe9e1}.quota-track span{display:block;height:100%;border-radius:inherit;background:#248a59;transition:width .4s ease}.quota-track span.warning{background:#dc9b21}.quota-track span.danger{background:#d5523c}.quota-footer{align-items:center;margin-top:14px}.quota-actions{display:flex;gap:9px}.quota-actions a{padding:9px 13px;border-radius:9px;font-size:13px;font-weight:800;text-decoration:none}.quota-actions .primary{background:#0d6542;color:#fff}.quota-actions .secondary{border:1px solid #b9cfc0;color:#165b3f}.quota-message{padding:24px 0;color:#61746a}.quota-message.warning{color:#a26012}@media(max-width:720px){.traffic-quota-card{padding:18px}.quota-heading,.quota-footer{display:block}.quota-numbers{grid-template-columns:1fr}.quota-status{display:inline-block;margin-top:12px}.quota-actions{margin-top:14px}.quota-heading h2{font-size:21px}}
</style>
